// Ensure App namespace exists
var App = App || {};

App.Base = (() => {
    'use strict';

    /* ========================================================================
    State Initialization
    ========================================================================== */

    const bootstrapElements = {
        accordionElements: [],
        tabElements: []
    };
    let modalUrls = [];
    let isModalStackedFromParent = false;

    const speedBumpModal = document.getElementById("SpeedBump");
    const speedBumpExemptHosts = typeof SpeedbumpData !== 'undefined'
        ? [...SpeedbumpData.ExemptList]
        : [];

    /* ========================================================================
    Bootstrap Elements Handling
    ========================================================================== */

    // Initializes Bootstrap element arrays and activates any element referenced by the current URL hash.
    const initializeBootstrapElements = () => {
        bootstrapElements.accordionElements = [...document.querySelectorAll("[data-bs-toggle='collapse']")]
            .filter(element => element.id)
            .map(element => `#${element.id}`);

        bootstrapElements.tabElements = [...document.querySelectorAll("[data-bs-toggle='tab']")]
            .filter(element => element.id)
            .map(element => `#${element.id}`);

        activateBootstrapElementFromHash(window.location.hash);
    };

    // Activates a Bootstrap element (accordion/tab) based on the given hash, if present in the DOM.
    const activateBootstrapElementFromHash = (candidate) => {

        const pathName = window.location.pathname;
        let typeFound = null;

        if (!candidate) return false;
        if (candidate.toLowerCase().startsWith(pathName.toLowerCase())) {
            candidate = candidate.slice(pathName.length);
        }

        if (!candidate.startsWith("#")) return false;

        const hash = candidate;

        for (const key in bootstrapElements) {
            if (bootstrapElements[key].includes(hash)) {
                typeFound = key;
                break;
            }
        }
        if (!typeFound) return false;

        // Main logic
        if (typeFound === "accordionElements") {

            const collapseElement = document.querySelector(`${hash}_Panel`);
            if (!collapseElement) return false;

            const bootstrapCollapse = bootstrap.Collapse.getOrCreateInstance(collapseElement, { toggle: true });
            bootstrapCollapse.show();

            const accordionItemElement = collapseElement.closest(".accordion-item");
            const accordionButtonElement = accordionItemElement.querySelector(`[data-bs-target]`);
            if (!accordionItemElement || !accordionButtonElement) return false;

            smoothScrollToElement(accordionItemElement);
            accordionButtonElement.focus();
            return true;
        }

        if (typeFound === "tabElements") {

            const tabElement = document.querySelector(hash);
            if (!tabElement) return false;

            const bootstrapTab = bootstrap.Tab.getOrCreateInstance(tabElement);
            bootstrapTab.show();
            tabElement.focus();

            smoothScrollToElement(tabElement);
            return true;
        }

        return false;
    };

    // Smoothly scrolls the page to the given element, offset by a specified number of pixels.
    const smoothScrollToElement = (element) => {
        const top = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    /* ========================================================================
    Modals
    ========================================================================== */

    // Loads modal URLs from the server and attempts to load a modal if the URL hash matches.
    const loadModalUrls = () => {
        fetch("/Api/Modals/GetModalUrls")
            .then(res => res.json())
            .then(urls => {
                modalUrls = [...urls];
                loadModal(window.location.hash.slice(1), null);
            })
            .catch(err => console.error(`Error fetching modal URLs: ${err}`));
    };

    // Returns the known modal URL matching the candidate, ignoring case and leading/trailing slashes (e.g. "#Modals/Foo" matches "/modals/foo/").
    const findModalUrl = (candidate) => {
        if (!candidate || !Array.isArray(modalUrls)) return null;
        const trimSlashes = (value) => value.replace(/^\/+|\/+$/g, "").toLowerCase();
        const normalized = trimSlashes(candidate);
        if (!normalized) return null;
        return modalUrls.find(url => trimSlashes(url) === normalized) ?? null;
    };

    // Loads and displays a modal or offcanvas element from the given URL, if present in the modalUrls list.
    const loadModal = (candidateHref, clickedElement) => {

        const targetHref = findModalUrl(candidateHref);
        if (!targetHref) return false;

        // The header tells Content_ModalController this is a modal load, not a direct visit (which it redirects)
        fetch(targetHref, { headers: { "X-Modal-Request": "true" } })
            .then(res => res.text())
            .then(html => {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = html;

                const modalElement = tempDiv.querySelector(".modal, .offcanvas");
                if (!modalElement) return;

                document.body.appendChild(modalElement);

                // Execute scripts inside modal content
                modalElement.querySelectorAll("script").forEach(script => {
                    const newScript = document.createElement("script");
                    if (script.src) {
                        newScript.src = script.src;
                        newScript.async = script.async;
                    } else {
                        newScript.textContent = script.textContent;
                    }
                    script.parentNode.replaceChild(newScript, script);
                });

                let bootstrapInstance, hiddenEventName;
                if (modalElement.classList.contains("modal")) {
                    bootstrapInstance = bootstrap.Modal.getOrCreateInstance(modalElement);
                    hiddenEventName = "hidden.bs.modal";
                } else if (modalElement.classList.contains("offcanvas")) {
                    bootstrapInstance = bootstrap.Offcanvas.getOrCreateInstance(modalElement);
                    hiddenEventName = "hidden.bs.offcanvas";
                }

                bootstrapInstance.show();

                modalElement.addEventListener(hiddenEventName, () => {
                    if (clickedElement) {
                        clickedElement.focus();
                        clickedElement.blur();
                    }
                    if (!isModalStackedFromParent) modalElement.remove();
                });
            })
            .catch(err => console.error(`Error loading modal content: ${err}`));

        return true;
    };

    // Shows an in-page modal or offcanvas whose id matches the href's hash (e.g. #login-menu), on the current page only.
    const showInPageModal = (href, clickedElement) => {
        if (!href) return false;

        let url;
        try {
            url = new URL(href, window.location.href);
        } catch {
            return false;
        }
        if (url.hostname !== window.location.hostname || url.pathname !== window.location.pathname || url.hash.length < 2) return false;

        const modalElement = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (!modalElement) return false;

        const isModal = modalElement.classList.contains("modal");
        if (!isModal && !modalElement.classList.contains("offcanvas")) return false;

        // Close the modal/offcanvas the link was clicked from (e.g. the mobile menu) so the two don't stack
        const parent = clickedElement?.closest(".modal.show, .offcanvas.show");
        if (parent && parent !== modalElement) {
            const ParentType = parent.classList.contains("modal") ? bootstrap.Modal : bootstrap.Offcanvas;
            ParentType.getOrCreateInstance(parent).hide();
        }

        const BootstrapType = isModal ? bootstrap.Modal : bootstrap.Offcanvas;
        // Pass the link as relatedTarget so show/shown listeners know what opened it
        BootstrapType.getOrCreateInstance(modalElement).show(clickedElement || undefined);
        return true;
    };

    // Opens an in-page modal/offcanvas if the page was loaded with its id as the hash, then clears the hash so a refresh doesn't reopen it.
    const showInPageModalFromHash = () => {
        if (!showInPageModal(window.location.href, null)) return;
        history.replaceState(null, "", window.location.pathname + window.location.search);
    };

    /* ========================================================================
    SpeedBump
    ========================================================================== */

    // Returns true if the hostname matches an exempt domain or is a subdomain of one (e.g. "speedpay.com" covers "internet.speedpay.com").
    const isSpeedBumpExempt = (hostname) => {
        const host = (hostname || "").toLowerCase();
        return speedBumpExemptHosts.some(exempt => host === exempt || host.endsWith(`.${exempt}`));
    };

    // Handles the display and logic for the external link speed bump modal.
    const handleSpeedBump = (clickedElement) => {

        const hostname = clickedElement.hostname;
        const targetHref = clickedElement.href;
        const modalParent = clickedElement.closest(".modal, .offcanvas");
        const ParentType = modalParent?.classList.contains("offcanvas") ? bootstrap.Offcanvas : bootstrap.Modal;

        if (!clickedElement || !speedBumpModal) return false;
        if (!targetHref || hostname === window.location.hostname || isSpeedBumpExempt(hostname) || isSpecialUriScheme(targetHref)) {
            return false;
        }

        // Set modal stack state: true if this was triggered from within a modal/offcanvas (e.g. the mobile menu)
        isModalStackedFromParent = !!modalParent;
        if (modalParent) ParentType.getOrCreateInstance(modalParent).hide();

        // Show speed bump
        const bootstrapSpeedBump = bootstrap.Modal.getOrCreateInstance(speedBumpModal);
        bootstrapSpeedBump.show();

        // Assign (not add) the handler so a cancelled speed bump doesn't leave a stale listener that opens the old URL later
        const proceedButton = speedBumpModal.querySelector("button[data-speedbump='proceed']");
        if (proceedButton) {
            proceedButton.onclick = () => {
                bootstrapSpeedBump.hide();
                window.open(targetHref);
            };
        }

        speedBumpModal.addEventListener("hidden.bs.modal", () => {
            if (clickedElement) {
                clickedElement.focus();
                clickedElement.blur();
            }

            // If we hid a parent modal, restore it and reset the stack state
            if (isModalStackedFromParent && modalParent) {
                ParentType.getOrCreateInstance(modalParent).show();
                isModalStackedFromParent = false; // Reset state after restoring
            }
        }, { once: true });

        return true;
    };

    /* ========================================================================
    Eltropy Chat
    ========================================================================== */

    const openChatHash = "#open-chat";
    const openChatPath = "/open-chat";

    // Returns true if the href points at the chat trigger (#open-chat or /open-chat, relative or absolute on this host).
    const isOpenChatHref = (href) => {
        if (!href) return false;
        try {
            const url = new URL(href, window.location.href);
            if (url.hostname !== window.location.hostname) return false;
            return url.hash.toLowerCase() === openChatHash
                || url.pathname.toLowerCase().replace(/\/$/, "") === openChatPath;
        } catch {
            return false;
        }
    };

    // Opens the Eltropy chat window, waiting (up to ~15s) for the widget to finish loading if needed.
    const openChat = () => {
        const maxAttempts = 60;
        let attempts = 0;

        const tryOpen = () => {
            // The Eltropy launcher button only exists once the widget (and its message listener) is ready.
            if (document.getElementById("eltropy-liveHelpButton")) {
                window.postMessage({ type: "ELTROPY_OPEN_BRIDGE" }, window.location.origin);
                return;
            }
            if (++attempts < maxAttempts) {
                setTimeout(tryOpen, 250);
            } else {
                // Eltropy's config endpoint returns 403 for localhost/127.0.0.1 origins, so the widget never initializes locally.
                const localHint = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
                    ? " Eltropy rejects localhost origins - use a hosts-file alias (e.g. mainstreetcu.local) to test locally."
                    : " Check the msg.mainstreetcu.org widget/config request in the network tab.";
                console.error(`Eltropy chat widget did not initialize.${localHint}`);
            }
        };

        tryOpen();
    };

    // Opens the chat if the page was loaded with #open-chat, then clears the hash so a refresh doesn't reopen it.
    const openChatFromHash = () => {
        if (window.location.hash.toLowerCase() !== openChatHash) return;
        history.replaceState(null, "", window.location.pathname + window.location.search);
        openChat();
    };

    /* ========================================================================
    Click Routing
    ========================================================================== */

    // Routes anchor clicks to the chat trigger, Bootstrap hash activation, in-page modals, modal loading, or speed bump logic.
    const handleAnchorClick = (element, event) => {
        event.stopPropagation();
        const targetHref = element.getAttribute("href");
        if (isOpenChatHref(targetHref)) {
            openChat();
            return event.preventDefault();
        }
        if (activateBootstrapElementFromHash(targetHref)) return event.preventDefault();
        if (showInPageModal(targetHref, element)) return event.preventDefault();
        if (loadModal(targetHref, element)) return event.preventDefault();
        if (handleSpeedBump(element)) return event.preventDefault();
    };

    /* ========================================================================
    Animation & Scroll Effects
    ========================================================================== */

    // Initialize scroll-triggered animations using IntersectionObserver
    const initScrollAnimations = () => {
        const osAnimationElements = document.querySelectorAll('.os-animation');
        onScrollInit(osAnimationElements);

        document.querySelectorAll('.staggered-animation').forEach(element => {
            const triggerElement = element.closest('.staggered-animation-container');
            onScrollInit([element], triggerElement);
        });
    };

    // Set up IntersectionObserver for scroll-triggered animations
    const onScrollInit = (items, trigger) => {
        const itemsArray = Array.isArray(items) ? items : Array.from(items);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    const osAnimationClass = element.getAttribute('data-os-animation');
                    const osAnimationDelay = element.getAttribute('data-os-animation-delay');

                    if (osAnimationDelay) {
                        element.style.webkitAnimationDelay = osAnimationDelay;
                        element.style.mozAnimationDelay = osAnimationDelay;
                        element.style.animationDelay = osAnimationDelay;
                    }

                    element.addEventListener('animationend', function handleAnimationEnd() {
                        element.classList.add('animation-complete');
                        element.classList.remove('animate__animated');
                        element.removeEventListener('animationend', handleAnimationEnd);
                    });

                    element.classList.add('animate__animated');
                    if (osAnimationClass) {
                        element.classList.add(osAnimationClass);
                    }

                    // Stop observing once animated
                    observer.unobserve(element);
                }
            });
        }, {
            threshold: 0.1, // 10% visible
            rootMargin: '0px 0px -10% 0px' // Similar to 90% offset in Waypoint
        });

        itemsArray.forEach(element => {
            observer.observe(element);
        });
    };

    /* ========================================================================
    Helpers
    ========================================================================== */

    // Returns true if the href uses a special URI scheme (javascript:, mailto:, tel:)
    const isSpecialUriScheme = (href) => {
        const schemes = ["javascript:", "mailto:", "tel:"];
        return schemes.some(scheme => href.startsWith(scheme));
    };

    // Makes tables responsive by wrapping them in a div.table-responsive if not already wrapped.
    const makeTablesResponsive = (selector) => {
        const tables = selector instanceof NodeList ? [...selector] : [selector];
        tables.forEach(table => {
            // Skip tables not yet in the DOM
            const { parentElement, parentNode } = table;
            if (!parentElement) return;

            // Skip if already wrapped
            if (!parentElement.classList.contains("table-responsive")) {
                const wrapper = document.createElement("div");
                wrapper.className = "table-responsive";
                parentNode.insertBefore(wrapper, table);
                wrapper.appendChild(table);
            }
        });
    };

    // Retrieves a URL parameter value by name from the current window location.
    const getUrlParameter = (name) => {
        const regex = new RegExp(`[?&]${name}=([^&#]*)`);
        const results = regex.exec(window.location.search);
        return results ? decodeURIComponent(results[1].replace(/\+/g, ' ')) : '';
    };

    /* ========================================================================
    Initialization
    ========================================================================== */

    // Initialize Bootstrap elements, modal URLs, and responsive tables on DOMContentLoaded
    document.addEventListener("DOMContentLoaded", () => {
        App.Base.initializeBootstrapElements();
        App.Base.loadModalUrls();
        App.Base.makeTablesResponsive(document.querySelectorAll(".rich-text table.table"));
        App.Base.initScrollAnimations();
        App.Base.openChatFromHash();
        App.Base.showInPageModalFromHash();

        // Refresh animations when Bootstrap tabs are shown
        document.querySelectorAll('[data-bs-toggle="tab"]').forEach(tabEl => {
            tabEl.addEventListener('shown.bs.tab', function (e) {
                App.Base.initScrollAnimations();
            });
        });
    });

    // Event delegation for anchor clicks
    // Attach a single click handler to document.body
    document.body.addEventListener("click", function(event) {
        const anchor = event.target.closest("a");
        if (!anchor) return;
        handleAnchorClick(anchor, event);
    });

    /* ========================================================================
    Public API Exposure
    ========================================================================== */

    return {
        initializeBootstrapElements,
        loadModalUrls,
        makeTablesResponsive,
        getUrlParameter,
        initScrollAnimations,
        openChat,
        openChatFromHash,
        showInPageModal,
        showInPageModalFromHash
    };

})();